import { ensureJourneyDb, getUserByEmail, insertRow, writeAudit } from "../db";
import { setCredential } from "../db/credentials";
import { assignFreshClientCode, issueClientToken } from "./clientAccess";
import { logClientActivity } from "./clientActivity";
import { createThread, createTransaction, createUser } from "../db/schema";
import { createBuyer } from "./buyersApi";
import { buyerFileId, sellerFileId } from "./filePacket/ids";

const EMPTY_SELLER_PORTFOLIO = {
  welcome: {},
  propertyDetails: {},
  interiorFeatures: {},
  exteriorFeatures: {},
  upgrades: {},
  conditionNotes: {},
  photos: {},
  improvements: {},
  listingReadiness: {},
  tips: {},
  pageNotes: {},
  notes: { forAgent: "", showing: "", disclosures: "" },
  portfolioSummary: {},
};

function appendSellerToList(record) {
  let list = [];
  try {
    list = JSON.parse(localStorage.getItem("dh_sellers") || "[]");
  } catch {
    list = [];
  }
  const index = list.findIndex((item) => item.id === record.id || item.email === record.email);
  if (index === -1) list.push(record);
  else list[index] = { ...list[index], ...record };
  localStorage.setItem("dh_sellers", JSON.stringify(list));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("dh-seller-change"));
  }
}

/**
 * Create a buyer or seller File on the signed-in agent/broker desk.
 * Buyer and seller records stay isolated — never mixed.
 */
export async function createDeskClient({
  session,
  kind,
  name,
  email,
  phone,
  address = "",
  notes = "",
  assignedAgentId = "",
  password = "",
}) {
  const role = kind === "seller" ? "seller" : kind === "buyer" ? "buyer" : null;
  if (!role) throw new Error("Choose buyer or seller.");
  const trimmedName = String(name || "").trim();
  const trimmedEmail = String(email || "").trim().toLowerCase();
  const trimmedPhone = String(phone || "").trim();
  const trimmedAddress = String(address || "").trim();
  const trimmedNotes = String(notes || "").trim();
  if (!trimmedName || !trimmedEmail) throw new Error("Name and email are needed.");
  if (!session?.userId) throw new Error("Sign in to your Desk first.");

  ensureJourneyDb();
  if (getUserByEmail(trimmedEmail)) {
    throw new Error("That email is already on a desk.");
  }

  const agentId = assignedAgentId || session.userId;
  const brokerId = session.role === "broker" ? session.userId : session.brokerageId || null;
  const client = insertRow(
    "users",
    createUser({
      role_type: role,
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      status: "active",
      brokerage_id: brokerId,
      office_name: session.office_name || "",
      linked_agent_name: session.role === "agent" ? session.name : "",
      linked_broker_name: session.role === "broker" ? session.name : "",
      client_code: assignFreshClientCode(role),
      notes: trimmedNotes,
      assigned_agent_id: agentId,
    })
  );

  if (role === "buyer") {
    await createBuyer({
      id: client.id,
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      agentId,
      password: password || undefined,
      status: "active",
    });
  } else {
    appendSellerToList({
      id: client.id,
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      agentId,
      agentName: session.role === "agent" ? session.name : "",
      brokerName: session.role === "broker" ? session.name : "",
      office: session.office_name || "",
      status: "active",
      portfolio: { ...EMPTY_SELLER_PORTFOLIO },
    });
  }

  let transaction = null;
  try {
    transaction = insertRow(
      "transactions",
      createTransaction({
        broker_id: brokerId || agentId,
        agent_id: agentId,
        client_id: client.id,
        client_role: role,
        property_address: trimmedAddress,
        status: "open",
      })
    );
    insertRow(
      "message_threads",
      createThread({
        transaction_id: transaction.id,
        created_by_user_id: agentId,
        status: "active",
      })
    );
  } catch {
    transaction = null;
  }

  if (password) {
    try {
      setCredential(client.id, password);
    } catch {
      /* ignore */
    }
  }

  writeAudit({
    user_id: session.userId,
    action_type: role === "buyer" ? "client.buyer.create" : "client.seller.create",
    transaction_id: transaction?.id || null,
    metadata: { client_id: client.id, client_role: role, client_code: client.client_code },
  });
  logClientActivity(client.id, "registered", {
    actorId: session.userId,
    kind: role,
    clientCode: client.client_code,
    transactionId: transaction?.id || null,
  });

  return {
    client,
    transaction,
    fileId: role === "seller" ? sellerFileId(client.id) : buyerFileId(client.id),
    token: issueClientToken(client.id),
  };
}
